/**
 * "Server relief" password hashing: the slow KDF runs in the browser, the server only
 * stores HMAC(pepper, derivedKey). Salt is derived from the normalised e-mail, so no
 * pre-login salt lookup (and no account-enumeration oracle) is needed.
 *
 * Changing these parameters requires a new KDF version (users.kdf_v).
 */
import { normalizeEmail } from './email';

export const CURRENT_KDF_V = 1;
export const KDF_PARAMS: Record<number, { iterations: number; hash: 'SHA-256' }> = {
  1: { iterations: 600_000, hash: 'SHA-256' },
};
export const DERIVED_KEY_BYTES = 32;
export const MIN_PASSWORD_LENGTH = 10;

export function kdfSalt(email: string, v = CURRENT_KDF_V): string {
  return `kgr-pw-v${v}|${normalizeEmail(email)}`;
}

function toB64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Derive the 32-byte key sent to the server (base64url). Runs PBKDF2 via WebCrypto. */
export async function deriveLoginKey(email: string, password: string, v = CURRENT_KDF_V): Promise<string> {
  const params = KDF_PARAMS[v];
  if (!params) throw new Error(`Unknown KDF version ${v}`);
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey('raw', enc.encode(password.normalize('NFC')), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: params.hash, iterations: params.iterations, salt: enc.encode(kdfSalt(email, v)) },
    baseKey,
    DERIVED_KEY_BYTES * 8,
  );
  return toB64url(new Uint8Array(bits));
}

/** Very small client-side policy (the server never sees the password). */
const COMMON = new Set([
  'password123', '1234567890', 'qwertyuiop', 'lykilord123', 'karlakor123', 'grafarvogur',
  'password1234', '0123456789', '1111111111', 'abcdefghij', 'karlakór123',
]);
export function passwordProblem(pw: string): 'too_short' | 'too_common' | null {
  if (pw.length < MIN_PASSWORD_LENGTH) return 'too_short';
  if (COMMON.has(pw.toLowerCase())) return 'too_common';
  return null;
}
